import { ChevronDownIcon, XIcon } from "lucide-react";
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type FC,
  forwardRef,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AssistantModalPrimitive } from "@assistant-ui/react";

import errorFilledIconSrc from "../../assets/ErrorFilled.svg";
import phoebeAvatarSvg from "../../../public/phoebeLightBlueBG.svg";
import { focusElementPreventScroll, Thread, ThreadHeader } from "@/components/assistant-ui/thread";
import { useHostBottomObstructionHeights } from "@/components/assistant-ui/host-bottom-action-bar";
import type { FollowUpSuggestion, WidgetFeedbackSubmitPayload } from "@/runtime/socket/types";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const TOOLTIP_OPEN = "Minimize chat";
const TOOLTIP_CLOSED = "Open chat";
const TOOLTIP_MINIMIZED = "Open chat";
const FIGMA_SHELL_HEIGHT_CLASS = "h-[min(746px,calc(100dvh-112px))] max-h-[746px]";
const FIGMA_SHELL_WIDTH_CLASS = "w-[408px]";
const FIGMA_SHELL_CLASS = "aui-chat-shell";
const FIGMA_SHELL_SHADOW_CLASS =
  "shadow-[0_3px_3px_-2px_rgba(0,24,35,0.05),0_3px_14px_0_rgba(0,24,35,0.25)]";
const CHAT_PANEL_CLASS = FIGMA_SHELL_HEIGHT_CLASS + " " + FIGMA_SHELL_WIDTH_CLASS;
const ERROR_FILLED_ICON_SRC = errorFilledIconSrc as unknown as string;

const MODAL_CONTENT_CLASS =
  "aui-root aui-modal-content relative z-50 flex flex-col justify-center overflow-clip overscroll-contain border border-border bg-[#FFFFFF] p-0 text-popover-foreground outline-none " +
  FIGMA_SHELL_CLASS + " " + FIGMA_SHELL_SHADOW_CLASS + " " +
  "opacity-0 transition-opacity duration-200 data-[state=open]:opacity-100";

// Applied to AssistantModalPrimitive.Content itself when hidden.
// Uses `invisible` (visibility:hidden) + dimension collapse so the DOM node —
// and the Thread inside it — stays mounted (via forceMount). The Root is driven
// by `isOpen && !isMinimized` so Radix's focus-trap is disengaged while hidden;
// forceMount keeps the content in the DOM so the socket survives minimize.
// `!` (Tailwind important) overrides the conditional CHAT_HEIGHT_CLASS set on
// the same element.
const HIDDEN_CONTENT_CLASS =
  "invisible pointer-events-none !h-0 !min-h-0 !p-0 !border-0 !shadow-none overflow-hidden";
const MINIMIZE_BUTTON_SELECTOR = "[data-testid='aichat_window_close']";
const OPEN_OVERLAY_SELECTOR = [
  "[role='dialog']",
  "[role='menu']",
  "[role='listbox']",
].join(",");
const CLOSED_OVERLAY_SELECTOR = [
  "[role='dialog'][data-state='closed']",
  "[role='menu'][data-state='closed']",
  "[role='listbox'][data-state='closed']",
].join(",");
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");
export const LAUNCHER_NUDGE_DISMISSED_STORAGE_KEY = "phoebe_launcher_nudge_dismissed";

function dispatchLauncherNudgeStorageEvent(newValue: string | null): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new StorageEvent("storage", {
      key: LAUNCHER_NUDGE_DISMISSED_STORAGE_KEY,
      newValue,
    }));
  } catch {
    window.dispatchEvent(new Event("storage"));
  }
}

function hasDismissedLauncherNudge(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(LAUNCHER_NUDGE_DISMISSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function visibleFocusableElements(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
    if (
      element.tabIndex < 0 ||
      element.hasAttribute("disabled") ||
      element.getAttribute("aria-hidden") === "true" ||
      element.closest("[inert], [aria-hidden='true']") ||
      element.closest(CLOSED_OVERLAY_SELECTOR)
    ) {
      return false;
    }
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  });
}

function hostFocusableElements(widgetRoot: HTMLElement): HTMLElement[] {
  return visibleFocusableElements(document).filter((element) => {
    if (widgetRoot.contains(element) || element.hasAttribute("data-radix-focus-guard")) return false;
    return !element.closest("[data-radix-popper-content-wrapper], [data-radix-portal]");
  });
}

export function resolveLauncherNudge(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LAUNCHER_NUDGE_DISMISSED_STORAGE_KEY, "true");
    dispatchLauncherNudgeStorageEvent("true");
  } catch {
    // Embedded host pages and privacy modes can block storage. The local caller
    // still updates React state so the nudge resolves for the current view.
  }
}

export function resetLauncherNudgeResolution(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(LAUNCHER_NUDGE_DISMISSED_STORAGE_KEY);
  } catch {
    // Best-effort QA utility; blocked storage should not break the widget.
  }
  dispatchLauncherNudgeStorageEvent(null);
}

export type WindowContentState = "ready" | "loading" | "error";

type AssistantModalProps = {
  backendUrl?: string;
  canCancelActiveTurn?: boolean;
  chatState: "idle" | "chat";
  isMinimized: boolean;
  unreadCount?: number;
  unreadBoundaryMessageId?: string | null;
  windowContentState?: WindowContentState;
  onButtonClick: () => void;
  onMinimize: () => void;
  onReopen: () => void;
  onNewChat?: () => void | Promise<void>;
  onEndLiveSession?: () => void;
  onSafetyExit?: () => void;
  onAssistantFeedback?: (payload: Omit<WidgetFeedbackSubmitPayload, "version" | "sourceId" | "updatedAt">) => void;
  onScheduleConsultation?: () => void;
  asyncSuggestions?: Map<string, FollowUpSuggestion[]>;
};

export const AssistantModal: FC<AssistantModalProps> = ({
  backendUrl,
  canCancelActiveTurn = true,
  chatState,
  isMinimized,
  unreadCount = 0,
  unreadBoundaryMessageId = null,
  windowContentState = "ready",
  onButtonClick,
  onMinimize,
  onReopen,
  onNewChat,
  onEndLiveSession,
  onSafetyExit,
  onAssistantFeedback,
  onScheduleConsultation,
  asyncSuggestions,
}) => {
  const isOpen = chatState === "chat";
  const [isNudgeDismissed, setIsNudgeDismissed] = useState(hasDismissedLauncherNudge);
  // The launcher is visible while the chat is closed or minimized, so it
  // needs the host-footer measurement too; otherwise it can overlap mobile
  // action bars before the chat opens.
  const {
    bottomOffsetHeight: hostBottomActionBarHeight,
    privacyFooterHeight: hostBottomPrivacyFooterHeight,
  } = useHostBottomObstructionHeights(true);
  const fabRef = useRef<HTMLButtonElement>(null);
  const chatHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  // Once the chat panel has been opened, keep the content force-mounted so
  // the Thread and socket survive minimize/reopen cycles.
  const hasEverOpenedRef = useRef(false);
  if (isOpen) hasEverOpenedRef.current = true;
  const isContentHidden = !isOpen || isMinimized;

  const handleMinimize = useCallback(() => {
    onMinimize();
    // Move focus to the FAB so keyboard users are not left on a
    // visibility:hidden element inside the collapsed panel.
    requestAnimationFrame(() => { fabRef.current?.focus(); });
  }, [onMinimize]);

  const getChatWindow = useCallback(() => {
    return contentRef.current ?? document.querySelector<HTMLElement>("[data-testid='aichat_window'], .aui-modal-content");
  }, []);

  const hasOpenChatOverlay = useCallback(() => {
    const chatWindow = getChatWindow();
    if (!chatWindow) return false;
    return Array.from(chatWindow.querySelectorAll<HTMLElement>(OPEN_OVERLAY_SELECTOR)).some(
      (element) => element.getAttribute("data-state") !== "closed",
    );
  }, [getChatWindow]);

  const triggerHeaderMinimizeButton = useCallback(() => {
    const minimizeButton = getChatWindow()?.querySelector<HTMLButtonElement>(MINIMIZE_BUTTON_SELECTOR);
    if (minimizeButton && !minimizeButton.disabled) {
      minimizeButton.click();
      return;
    }
    handleMinimize();
  }, [getChatWindow, handleMinimize]);

  const isEscapeEventInChat = useCallback((event: globalThis.KeyboardEvent) => {
    const chatWindow = getChatWindow();
    if (!chatWindow) return false;
    const target = event.target;
    if (target instanceof Node && chatWindow.contains(target)) return true;
    const activeElement = document.activeElement;
    return activeElement instanceof Node && chatWindow.contains(activeElement);
  }, [getChatWindow]);

  const handleContentKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || isContentHidden || event.defaultPrevented) return;
    if (hasOpenChatOverlay()) return;
    event.preventDefault();
    event.stopPropagation();
    triggerHeaderMinimizeButton();
  };

  useEffect(() => {
    if (!isOpen || isContentHidden) return;

    const handleDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (!isEscapeEventInChat(event)) return;
      if (hasOpenChatOverlay()) return;
      event.preventDefault();
      event.stopPropagation();
      triggerHeaderMinimizeButton();
    };

    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [hasOpenChatOverlay, isEscapeEventInChat, isOpen, isContentHidden, triggerHeaderMinimizeButton]);

  useEffect(() => {
    if (!isOpen || isContentHidden) return;

    const loopFocusInsideChat = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Tab") return;
      // Nested chat dialogs (feedback and new chat) own their focus traps.
      // Let their Radix dialog scope handle Tab instead of cycling the parent
      // chat controls underneath them.
      if (hasOpenChatOverlay()) return;

      // Radix's non-modal focus scope can prevent the browser event before
      // this window capture listener runs, but its candidate list differs by
      // engine. The visible chat owns its complete Tab sequence here.

      const chatWindow = getChatWindow();
      const activeElement = document.activeElement;
      if (!chatWindow || !(activeElement instanceof Node) || !chatWindow.contains(activeElement)) return;

      const focusableElements = visibleFocusableElements(chatWindow);
      if (focusableElements.length === 0) return;

      const direction = event.shiftKey ? -1 : 1;
      const currentIndex = focusableElements.indexOf(activeElement as HTMLElement);
      const isProgrammaticallyFocusedHeading =
        activeElement instanceof HTMLElement &&
        activeElement.matches("h1, h2, h3, h4, h5, h6") &&
        activeElement.tabIndex === -1;
      if (currentIndex === -1 && !isProgrammaticallyFocusedHeading) return;

      // A programmatically focused, non-tabbable heading has no position in
      // the browser's sequential Tab order (most visibly in WebKit). Treat
      // that initial accessibility focus as immediately before/after the
      // modal's visible tab order in every engine.
      const nextIndex = currentIndex === -1
        ? (event.shiftKey ? focusableElements.length - 1 : 0)
        : (currentIndex + direction + focusableElements.length) % focusableElements.length;

      event.preventDefault();
      // The parent dialog is intentionally non-modal so host content remains
      // reachable after minimize. Its Radix focus scope still tries to wrap
      // on Tab, though, and skips the final legal links in some browsers.
      // Keep the complete in-dialog sequence under this loop's ownership.
      event.stopPropagation();
      focusableElements[nextIndex]?.focus({ preventScroll: true });
    };

    window.addEventListener("keydown", loopFocusInsideChat, true);
    return () => window.removeEventListener("keydown", loopFocusInsideChat, true);
  }, [getChatWindow, hasOpenChatOverlay, isContentHidden, isOpen]);

  useEffect(() => {
    if (!isMinimized) return;

    const handOffLauncherTabToHost = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Tab" || event.defaultPrevented || document.activeElement !== fabRef.current) return;

      const widgetRoot = document.getElementById("phoebe-chat-root");
      if (!widgetRoot) return;

      const hostElements = hostFocusableElements(widgetRoot);
      if (hostElements.length === 0) return;

      const isBeforeWidget = (element: HTMLElement) =>
        (widgetRoot.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING) !== 0;
      const isAfterWidget = (element: HTMLElement) =>
        (widgetRoot.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
      const target = event.shiftKey
        ? [...hostElements].reverse().find(isBeforeWidget)
        : hostElements.find(isAfterWidget);
      const fallbackTarget = event.shiftKey ? hostElements[hostElements.length - 1] : hostElements[0];

      event.preventDefault();
      (target ?? fallbackTarget)?.focus({ preventScroll: true });
    };

    document.addEventListener("keydown", handOffLauncherTabToHost, true);
    return () => document.removeEventListener("keydown", handOffLauncherTabToHost, true);
  }, [isMinimized]);

  useEffect(() => {
    if (!isOpen || isContentHidden) return undefined;
    const root = document.documentElement;
    const visualViewport = window.visualViewport;

    const syncVisualViewport = () => {
      const viewportHeight = visualViewport?.height || window.innerHeight;
      const viewportWidth = visualViewport?.width || window.innerWidth;
      root.style.setProperty(
        "--phoebe-mobile-visual-viewport-height",
        `${Math.round(viewportHeight)}px`,
      );
      root.style.setProperty(
        "--phoebe-mobile-visual-viewport-left",
        `${Math.round(visualViewport?.offsetLeft ?? 0)}px`,
      );
      root.style.setProperty(
        "--phoebe-mobile-visual-viewport-top",
        `${Math.round(visualViewport?.offsetTop ?? 0)}px`,
      );
      root.style.setProperty(
        "--phoebe-mobile-visual-viewport-width",
        `${Math.round(viewportWidth)}px`,
      );
    };

    syncVisualViewport();
    visualViewport?.addEventListener("resize", syncVisualViewport);
    visualViewport?.addEventListener("scroll", syncVisualViewport);
    window.addEventListener("resize", syncVisualViewport);

    return () => {
      visualViewport?.removeEventListener("resize", syncVisualViewport);
      visualViewport?.removeEventListener("scroll", syncVisualViewport);
      window.removeEventListener("resize", syncVisualViewport);
      for (const property of [
        "--phoebe-mobile-visual-viewport-height",
        "--phoebe-mobile-visual-viewport-left",
        "--phoebe-mobile-visual-viewport-top",
        "--phoebe-mobile-visual-viewport-width",
      ]) {
        root.style.removeProperty(property);
      }
    };
  }, [isOpen, isContentHidden]);

  const dismissNudge = useCallback(() => {
    setIsNudgeDismissed(true);
    resolveLauncherNudge();
  }, []);

  const openFromNudge = useCallback(() => {
    setIsNudgeDismissed(true);
    resolveLauncherNudge();
    onButtonClick();
  }, [onButtonClick]);

  const openFromLauncher = useCallback(() => {
    setIsNudgeDismissed(true);
    resolveLauncherNudge();
    onButtonClick();
  }, [onButtonClick]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === LAUNCHER_NUDGE_DISMISSED_STORAGE_KEY || event.key == null) {
        setIsNudgeDismissed(hasDismissedLauncherNudge());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // FAB: three-state dispatch
  // - minimized → reopen (show FAB while panel is hidden)
  // - chat (not minimized) → minimize
  // - idle → open chat
  const fabDataState = isMinimized ? "minimized" : isOpen ? "open" : "closed";
  const fabTooltip = isMinimized ? TOOLTIP_MINIMIZED : isOpen ? TOOLTIP_OPEN : TOOLTIP_CLOSED;
  const fabClickHandler = isMinimized ? onReopen : isOpen ? handleMinimize : openFromLauncher;

  return (
    <AssistantModalPrimitive.Root open={isOpen && !isMinimized}>
      <AssistantModalPrimitive.Anchor
        className="aui-root aui-modal-anchor aui-chat-launcher-anchor"
        style={{
          "--aui-host-bottom-action-bar-height": `${hostBottomActionBarHeight}px`,
          "--aui-host-bottom-privacy-footer-height": `${hostBottomPrivacyFooterHeight}px`,
        } as CSSProperties}
      >
        {/* Show nudge only when fully closed (not minimized) */}
        {!isOpen && !isMinimized && (
          <>
            {!isNudgeDismissed && (
            <LauncherNudge
              onDismiss={dismissNudge}
              onOpen={openFromNudge}
            />
          )}
          </>
        )}
        {/* FAB is visible when closed OR minimized; hidden when open and not minimized */}
        {(!isOpen || isMinimized) && (
          <span className="aui-chat-launcher-button-slot">
            <AssistantModalPrimitive.Trigger asChild>
              <AssistantModalButton
                ref={fabRef}
                data-state={fabDataState}
                tooltip={fabTooltip}
                showTooltip={false}
                onClick={fabClickHandler}
              />
            </AssistantModalPrimitive.Trigger>
            <AssistantLauncherUnreadBadge unreadCount={unreadCount} />
          </span>
        )}
      </AssistantModalPrimitive.Anchor>
      {/* forceMount (once opened) keeps Thread and socket alive while minimized
          even though Root open is false. CSS collapse + aria-hidden hides the
          content visually and from the a11y tree. inert removes all descendants
          from the tab order so keyboard users cannot reach hidden controls. */}
      <AssistantModalPrimitive.Content
        ref={contentRef}
        portalProps={{ forceMount: hasEverOpenedRef.current || undefined }}
        sideOffset={16}
        style={{
          "--aui-host-bottom-action-bar-height": `${hostBottomActionBarHeight}px`,
          "--aui-host-bottom-privacy-footer-height": `${hostBottomPrivacyFooterHeight}px`,
        } as CSSProperties}
        className={cn(
          MODAL_CONTENT_CLASS,
          CHAT_PANEL_CLASS,
          isContentHidden && HIDDEN_CONTENT_CLASS,
        )}
        aria-label="Chat with us"
        aria-hidden={isContentHidden || undefined}
        data-testid="aichat_window"
        onOpenAutoFocus={(event) => {
          // The ready home view is mounted during background connection, so
          // direct Radix's initial focus to the heading instead of a body control.
          event.preventDefault();
          focusElementPreventScroll(chatHeadingRef.current);
        }}
        onEscapeKeyDown={(event) => {
          if (isContentHidden || event.defaultPrevented || !isEscapeEventInChat(event)) return;
          if (hasOpenChatOverlay()) return;
          event.preventDefault();
          event.stopPropagation();
          triggerHeaderMinimizeButton();
        }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(isContentHidden ? { inert: "" as any } : {})}
        aria-busy={windowContentState === "loading"}
        onKeyDown={handleContentKeyDown}
      >
        <div className="flex h-full min-h-0 w-full flex-col">
          <ThreadHeader
            headingRef={chatHeadingRef}
            onMinimize={handleMinimize}
            onNewChat={onNewChat}
            onScheduleConsultation={onScheduleConsultation}
          />
          <div className="flex min-h-0 w-full flex-[1_0_0] flex-col items-center justify-center self-stretch">
            {windowContentState === "loading" ? (
              <WindowLevelLoader />
            ) : windowContentState === "error" ? (
              <WindowLevelError />
            ) : (
              <Thread
                backendUrl={backendUrl}
                canCancelActiveTurn={canCancelActiveTurn}
                isVisible={!isContentHidden}
                unreadBoundaryMessageId={unreadBoundaryMessageId}
                showHeader={false}
                headingRef={chatHeadingRef}
                onMinimize={handleMinimize}
                onNewChat={onNewChat}
                onEndLiveSession={onEndLiveSession}
                onSafetyExit={onSafetyExit}
                onAssistantFeedback={onAssistantFeedback}
                onScheduleConsultation={onScheduleConsultation}
                asyncSuggestions={asyncSuggestions}
              />
            )}
          </div>
        </div>
      </AssistantModalPrimitive.Content>
    </AssistantModalPrimitive.Root>
  );
};

export const WindowLevelLoader: FC = () => (
  <div
    data-testid="aichat_window_loader"
    role="status"
    aria-label="Loading chat"
    aria-busy="true"
    className="flex h-full flex-1 flex-col items-center justify-center gap-[40px] self-stretch bg-white"
  >
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      className="size-[32px] animate-spin"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="16"
        cy="16"
        r="14"
        stroke="var(--primary-main, #DB3725)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="66 88"
      />
    </svg>
  </div>
);

export const WindowLevelError: FC = () => (
  <div className="flex h-full flex-col items-center justify-center bg-white">
    <div
      data-testid="aichat_window_error_window"
      role="alert"
      className="mx-auto flex w-[376px] max-w-[calc(100%-16px)] flex-col items-center justify-center gap-[8px] py-[4px] text-center font-['Roboto',sans-serif] text-[14px] font-normal leading-[20px] tracking-[0px] text-[#B21F13]"
      style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
    >
      <img src={ERROR_FILLED_ICON_SRC} alt="Error" className="size-[16.667px] shrink-0" />
      <p>I'm having trouble connecting right now. Please try again or call 1-866-766-0766 for additional assistance.</p>
    </div>
  </div>
);

export const LauncherNudge: FC<{ onDismiss: () => void; onOpen: () => void }> = ({ onDismiss, onOpen }) => (
  <div className="aui-chat-launcher-nudge-shell">
    <button
      type="button"
      className="aui-chat-launcher-nudge"
      data-testid="aichat_message-preview"
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
        event.preventDefault();
        onOpen();
      }}
    >
      <span className="aui-chat-launcher-nudge-copy" data-testid="aichat_message-preview_generic">
        <span className="block aui-chat-launcher-nudge-title">Phoebe - AI assistant</span>
        <span className="block aui-chat-launcher-nudge-body">How can I help?</span>
      </span>
    </button>
    <button
      type="button"
      aria-label="Dismiss chat prompt"
      className="aui-chat-launcher-nudge-dismiss"
      data-testid="aichat_message-preview_close"
      onClick={onDismiss}
    >
      <XIcon className="size-[10px]" />
    </button>
  </div>
);

type AssistantModalButtonProps = ComponentPropsWithoutRef<"button"> & {
  "data-state"?: "open" | "closed" | "minimized";
  tooltip: string;
  showTooltip?: boolean;
};

type AssistantLauncherUnreadBadgeProps = {
  unreadCount?: number;
};

export function AssistantLauncherUnreadBadge({ unreadCount = 0 }: AssistantLauncherUnreadBadgeProps) {
  const normalizedUnreadCount = Math.max(0, Math.min(99, Math.floor(unreadCount)));

  if (normalizedUnreadCount <= 0) return null;

  return (
    <span
      key={normalizedUnreadCount}
      className="aui-chat-launcher-unread-badge"
      data-testid="aichat_unread-badge"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={`${normalizedUnreadCount} unread message${normalizedUnreadCount === 1 ? "" : "s"}`}
    >
      {normalizedUnreadCount > 9 ? "9+" : normalizedUnreadCount}
    </span>
  );
}

export const AssistantModalButton = forwardRef<
  HTMLButtonElement,
  AssistantModalButtonProps
>(({ "data-state": state, tooltip, showTooltip = true, className, style, type = "button", ...rest }, ref) => {
  const content = (
    <>
      <Avatar
        data-state={state}
        className="aui-chat-launcher-avatar aui-chat-launcher-avatar-wrapper data-[state=closed]:rotate-0 data-[state=minimized]:rotate-0 data-[state=open]:rotate-90 data-[state=closed]:scale-100 data-[state=minimized]:scale-100 data-[state=open]:scale-0"
      >
        <AvatarImage src={phoebeAvatarSvg as unknown as string} alt="" />
        <AvatarFallback aria-hidden="true">P</AvatarFallback>
      </Avatar>
      <ChevronDownIcon
        data-state={state}
        className="absolute size-6 text-gray-600 transition-all data-[state=closed]:-rotate-90 data-[state=minimized]:-rotate-90 data-[state=open]:rotate-0 data-[state=closed]:scale-0 data-[state=minimized]:scale-0 data-[state=open]:scale-100"
      />
    </>
  );
  const launcherClassName = cn("aui-modal-button aui-chat-launcher-button", className);
  const launcherStyle = { borderRadius: "80px", ...style };

  if (!showTooltip) {
    return (
      <button
        ref={ref}
        type={type}
        data-testid="aichat_launcher"
        {...rest}
        className={launcherClassName}
        style={launcherStyle}
      >
        {content}
        <span className="aui-sr-only sr-only">{tooltip}</span>
      </button>
    );
  }

  return (
    <TooltipIconButton
      variant="default"
      tooltip={tooltip}
      side="left"
      focusOnly
      data-testid="aichat_launcher"
      {...rest}
      type={type}
      className={launcherClassName}
      style={launcherStyle}
      ref={ref}
    >
      {content}
    </TooltipIconButton>
  );
});

AssistantModalButton.displayName = "AssistantModalButton";
